/* eslint-disable camelcase */
import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Table, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { TextModal, InternalLink, MitreSubtechniques, MitreExportButtons } from '@mdc/ui';
import { FileResultsService, getRandomID } from '@mdc/services';
import { processModalData, MINIMUM_TABLE_ROWS_DISPLAYED, TABLE_LEGEND } from './MitreData';

import './OpswatMitre.scss';

const OpswatMitre = ({ mitreData, activeMitreKey, sandboxResults, mitreValues }) => {
    const [buttonTooltipId] = useState(`MitreTooltip${getRandomID(5)}`);
    const [isModalDisplayed, setIsModalDisplayed] = useState(false);
    const [modalData, setModalData] = useState([]);
    const { t, ready } = useTranslation();

    const version = useMemo(() => mitreData && mitreData[0] && mitreData[0]?.metadata?.version || '', [mitreData]);

    const handleClose = () => {
        setIsModalDisplayed(false);
    };

    const renderTooltip = (props, techniqueId) => (
        <Tooltip id={buttonTooltipId} {...props} className='mitreTooltip'>
            {techniqueId}
        </Tooltip>
    );

    const modalDom = useMemo(() => {
        return <TextModal
            show={isModalDisplayed}
            title={modalData?.title}
            body={modalData?.body}
            onClose={handleClose}
            className='mitreAttackModal'
            size={'lg'}
        />;
    }, [mitreData, isModalDisplayed, modalData]);

    const handleTTPClick = (event, ttp) => {
        event.preventDefault();

        let tempModalData = {
            title: t('MITRE ATT&CK™ Technique') + ' - ' + ttp?.technique_id + ' - ' + ttp?.name,
        };

        setModalData(tempModalData);

        (async() => {
            try {
                const info = await FileResultsService.getMitreData(ttp?.technique_id, activeMitreKey);
                tempModalData = { ...tempModalData, body: processModalData(info, t) };
                setModalData(tempModalData);
            } catch (error) {
                // eslint-disable-next-line no-console
                console.log(error);
            }
        })();

        setIsModalDisplayed(true);
    };

    const processedTableData = useMemo(() => {
        return mitreData?.tactics?.reduce((acc, item) => {
            const { name, techniques } = item;
            acc[name] = [];
            mitreData?.map((mitre) => {
                if (!mitre?.hasOwnProperty('tactics')) {
                    const mitreTechniques = mitre?.techniques;
                    const isTechniqueIncluded = techniques?.filter((technique) => mitreTechniques.some((mitreTechnique) => technique?.id === mitreTechnique?.technique_id))?.length > 0;

                    if (isTechniqueIncluded) {
                        acc[name].push(mitre);
                    }
                }
            });

            return acc;
        }, {});
    }, [mitreData]);

    const tableNoOfRows = useMemo(() => {
        const arraysLengths = Object.keys(processedTableData).map((array) => processedTableData[array].length);
        const maxRows = Math.max(...arraysLengths);

        return maxRows < MINIMUM_TABLE_ROWS_DISPLAYED ? MINIMUM_TABLE_ROWS_DISPLAYED : maxRows;
    }, [processedTableData]);

    const opswatMitreHeader = useMemo(() => {
        const headline = <h2 className='h6Mask m-0'>{t('MITRE ATT&CK Matrix | version')}{' '}{version}</h2>;
        const fileName = mitreValues(t)?.filter((value) => value.key === activeMitreKey)[0]?.name;
        const actions = <MitreExportButtons
            types={['pdf', 'csv']}
            data={processedTableData}
            noOfRows={tableNoOfRows}
            fileName={sandboxResults?.data?.sha1 ? `${fileName}-${sandboxResults?.data?.sha1}` : `${fileName}`}
        />;

        return <div className='opswatMitreHeader d-flex flex-row align-items-center justify-content-between'>
            {headline}
            {actions}
        </div>;
    }, [mitreData, processedTableData, modalData, tableNoOfRows, sandboxResults, activeMitreKey, t]);

    const tableHeadDom = useMemo(() => {
        return mitreData?.tactics?.map((mitreTactic, index) => (
            <th key={index}>
                <div className='tacticName'>{mitreTactic?.name}</div>
                <span className='d-block'>
                    {'('}{processedTableData[mitreTactic?.name]?.length}{'/'}{mitreTactic?.techniques?.length}{')'}
                </span>
            </th>
        ));
    }, [mitreData]);

    const tableBodyDom = useMemo(() => {
        return Array(tableNoOfRows).fill('').map((row, index) => (
            <tr key={index}>
                {
                    Object.keys(processedTableData).map((data, index2) => {
                        if (processedTableData[data][index]) {
                            const {
                                subtechnique_id: subtechniqueId,
                                technique_id: techniqueId,
                                name: techniqueName,
                                permissions_required: permissionsRequired,
                                source_uri: sourceURI
                            } = processedTableData[data][index]?.techniques[0];
                            return <td key={index2} className={`hasData ${subtechniqueId ? 'hasChildren' : ''}`}>
                                <div className='tdWithChildrenWrapper'>
                                    <div className='techniqueLinkWrapper'>
                                        <OverlayTrigger
                                            placement='top'
                                            delay={{ show: 250, hide: 400 }}
                                            overlay={(props) => renderTooltip(props, techniqueId)}
                                        >
                                            <InternalLink to='/' onClick={(event) => handleTTPClick(event, processedTableData[data][index]?.techniques[0])}>{techniqueName}</InternalLink>

                                        </OverlayTrigger>
                                        <div className='d-none'>
                                            {/* Used for the export PDF functionality */}
                                            <p>{t('Technique ID')}{': '}{techniqueId}</p><br />
                                            <p>{t('Subtechnique ID')}{': '}{subtechniqueId || '-'}</p><br />
                                            <p>{t('Permissions Required')}{': '}{permissionsRequired?.join(', ') || '-'}</p><br />
                                            <p>{t('Source URI')}{': '}{<a href={sourceURI} rel='noopener noreferrer' target='_blank'>{sourceURI}</a>}</p><br />
                                        </div>
                                    </div>

                                    {subtechniqueId && <MitreSubtechniques
                                        techniqueId={techniqueId}
                                        subtechniqueId={subtechniqueId}
                                        techniques={processedTableData[data][index]?.techniques[0]}
                                        onSubtechniqueClick={handleTTPClick}
                                    />}
                                </div>
                            </td>;
                        }

                        return <td key={index2}></td>;
                    })
                }
            </tr>
        ));
    }, [tableNoOfRows, processedTableData, t]);

    const tableFooterDom = useMemo(() => {
        return <td colSpan={'100%'}>
            <div className='legend d-flex flex-row align-items-center'>
                {TABLE_LEGEND(t).map((legendItem, index) => {
                    return <div key={index} className={`${legendItem.key} legendItem`}>{legendItem.title}</div>;
                })}
            </div>
        </td>;
    }, [t]);

    const opswatMitreTable = useMemo(() => {
        return <Table className='opswatMitreTable'>
            <thead>
                <tr>{tableHeadDom}</tr>
            </thead>

            <tbody>{tableBodyDom}</tbody>

            <tfoot>
                <tr>{tableFooterDom}</tr>
            </tfoot>
        </Table>;
    }, [mitreData, processedTableData, tableNoOfRows, activeMitreKey, tableBodyDom, tableHeadDom, t]);

    if (!ready) {
        return;
    }

    return <div className='opswatMitre'>
        {opswatMitreHeader}
        <div className='opswatMitreTableWrapper'>
            {opswatMitreTable}
        </div>
        {modalDom}
    </div>;
};

OpswatMitre.propTypes = {
    mitreData: PropTypes.array,
    activeMitreKey: PropTypes.string,
    sandboxResults: PropTypes.object,
    mitreValues: PropTypes.func
};

export default OpswatMitre;
